<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  addHours,
  addMinutes,
  addMonths,
  format,
  isAfter,
  isBefore,
  nextMonday,
  setHours,
  setMinutes,
  setSeconds,
  startOfTomorrow,
} from 'date-fns';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useSocialPostsStore } from 'dashboard/stores/socialPosts';
import {
  checkFileSizeLimit,
  resolveMaximumFileUploadSize,
} from 'shared/helpers/FileHelper';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  inboxes: { type: Array, default: () => [] },
  isSubmitting: { type: Boolean, default: false },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();

// Mirrors Social::Facebook::PostPublisher::SCHEDULE_WINDOW exactly -- a
// client-side bound looser than the server's would let an agent submit a
// schedule the API then rejects; a bound tighter would silently remove
// capability the server actually allows.
const SCHEDULE_MIN_MINUTES = 10;
const SCHEDULE_MAX_MONTHS = 6;

// Facebook truncates a feed post behind "See more" at roughly this point, which is
// the only length in a post that changes how many people read it. The counter turns
// amber past it rather than blocking: going long is a choice, not an error.
const VISIBLE_CHARACTER_LIMIT = 125;

// `post_publishing` and `comment_management` clear Meta App Review
// independently (see Api::V1::Accounts::SocialPostsController), so an
// account can read comments and list posts while still lacking publish
// access. The composer has to gate itself on the narrower flag rather than
// relying on the Posts tab's own gate, which is comment_management only.
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const isPostPublishingEnabled = computed(() =>
  isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.POST_PUBLISHING
  )
);

const dialogRef = ref(null);

// The gate stays on the whole composer, not just its trigger: even if
// something calls open() directly (a stale ref, a future caller), a
// disabled account never sees the dialog's contents.
const open = () => {
  if (!isPostPublishingEnabled.value) return;
  dialogRef.value?.open();
};
const close = () => dialogRef.value?.close();

const inboxId = ref(undefined);
const message = ref('');
const link = ref('');

// Most accounts connect a single page, and making them pick it from a list of one is
// a step that only ever has one answer. Re-runs if the inbox list arrives after mount.
watch(
  () => props.inboxes,
  inboxes => {
    if (inboxes.length === 1) inboxId.value = inboxes[0].id;
  },
  { immediate: true }
);

const selectedInbox = computed(() =>
  props.inboxes.find(inbox => inbox.id === inboxId.value)
);

// The preview replaces the editor rather than sitting beside it: this composer is a
// single column, and a permanent side panel restating the caption verbatim was the
// thing that made the old layout feel cluttered. Toggling keeps both at full width.
const isPreviewing = ref(false);
const togglePreview = () => {
  isPreviewing.value = !isPreviewing.value;
};

const visibleCharacterCount = computed(() => message.value.trim().length);
const isPastTruncation = computed(
  () => visibleCharacterCount.value > VISIBLE_CHARACTER_LIMIT
);

const postsStore = useSocialPostsStore();

// Both AI actions read the caption the agent has already written -- there is no separate
// brief to fill in, so the only way to ask for something is to type it. Neither can submit:
// the copywriter writes back into `message` as editable text, and the image prompt only ever
// lands in `generatedImagePrompt`, a read-only value to copy elsewhere. This app calls no
// image model and produces no image.
const hasBrief = computed(() => Boolean(message.value.trim()));

const isGenerating = ref(false);
const generationError = ref('');

const handleGenerate = async () => {
  if (!hasBrief.value || isGenerating.value) return;

  generationError.value = '';
  isGenerating.value = true;
  try {
    message.value = await postsStore.generate({ brief: message.value.trim() });
  } catch (error) {
    generationError.value =
      error.message || t('COMMENTS.POSTS.COMPOSER.AI.ERROR');
  } finally {
    isGenerating.value = false;
  }
};

const isGeneratingImagePrompt = ref(false);
const imagePromptGenerationError = ref('');
const generatedImagePrompt = ref('');

const handleGenerateImagePrompt = async () => {
  if (!hasBrief.value || isGeneratingImagePrompt.value) return;

  imagePromptGenerationError.value = '';
  isGeneratingImagePrompt.value = true;
  try {
    generatedImagePrompt.value = await postsStore.generateImagePrompt({
      brief: message.value.trim(),
    });
  } catch (error) {
    imagePromptGenerationError.value =
      error.message || t('COMMENTS.POSTS.COMPOSER.IMAGE_PROMPT.ERROR');
  } finally {
    isGeneratingImagePrompt.value = false;
  }
};

const handleCopyImagePrompt = async () => {
  await copyTextToClipboard(generatedImagePrompt.value);
  useAlert(t('COMMENTS.POSTS.COMPOSER.IMAGE_PROMPT.COPIED'));
};

const dismissImagePrompt = () => {
  generatedImagePrompt.value = '';
  imagePromptGenerationError.value = '';
};

// A post is text, a link or an image -- never more than one at a time.
// Social::Facebook::PostPublisher only ever reads `link` when no image is
// attached (an image publish never sends `link` to Graph at all), so
// letting an agent fill in both invited a link that silently vanished the
// moment an image was also attached. Choosing an attachment therefore
// replaces whatever was attached before.
const postType = ref('text');

const isValidHttpUrl = value => {
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

const linkError = computed(() => {
  if (postType.value !== 'link' || !link.value.trim()) return '';
  return isValidHttpUrl(link.value.trim())
    ? ''
    : t('COMMENTS.POSTS.COMPOSER.LINK_ERROR');
});

// Same installation-configurable attachment limit conversation uploads use
// (useFileUpload.js), defaulting to DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE when
// unset -- a post image is an attachment like any other, so it gets no
// bespoke cap.
const globalConfig = useMapGetter('globalConfig/get');
const imageMaxSizeMB = computed(() =>
  resolveMaximumFileUploadSize(globalConfig.value?.maximumFileUploadSize)
);

const image = ref(null);
const imagePreviewUrl = ref('');
const imageError = ref('');

const video = ref(null);
const videoPreviewUrl = ref('');
const videoError = ref('');

const removeImage = () => {
  if (imagePreviewUrl.value) URL.revokeObjectURL(imagePreviewUrl.value);
  image.value = null;
  imagePreviewUrl.value = '';
  imageError.value = '';
};

const removeVideo = () => {
  if (videoPreviewUrl.value) URL.revokeObjectURL(videoPreviewUrl.value);
  video.value = null;
  videoPreviewUrl.value = '';
  videoError.value = '';
};

const selectPostType = type => {
  if (postType.value === type) return;
  if (postType.value === 'link') link.value = '';
  if (postType.value === 'image') removeImage();
  if (postType.value === 'video') removeVideo();
  postType.value = type;
};

// The attachment buttons toggle: pressing the active one clears the attachment
// and returns the post to plain text, so there is always a way back out.
const togglePostType = type =>
  selectPostType(postType.value === type ? 'text' : type);

const handleImageChange = event => {
  const file = event.target.files?.[0];
  // Allow re-selecting the same file after a rejection or removal.
  event.target.value = '';
  if (!file) return;

  if (!file.type.startsWith('image/')) {
    imageError.value = t('COMMENTS.POSTS.COMPOSER.IMAGE_ERROR.NOT_IMAGE');
    return;
  }
  if (!checkFileSizeLimit(file, imageMaxSizeMB.value)) {
    imageError.value = t('COMMENTS.POSTS.COMPOSER.IMAGE_ERROR.TOO_LARGE', {
      size: imageMaxSizeMB.value,
    });
    return;
  }

  if (imagePreviewUrl.value) URL.revokeObjectURL(imagePreviewUrl.value);
  imageError.value = '';
  image.value = file;
  imagePreviewUrl.value = URL.createObjectURL(file);
};

// Same attachment ceiling as an image -- a post video is an upload like any other, and the
// installation's configured limit is the only cap that matters. Facebook applies its own
// duration and format rules server-side; mirroring those here would drift from Meta's.
const handleVideoChange = event => {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;

  if (!file.type.startsWith('video/')) {
    videoError.value = t('COMMENTS.POSTS.COMPOSER.VIDEO_ERROR.NOT_VIDEO');
    return;
  }
  if (!checkFileSizeLimit(file, imageMaxSizeMB.value)) {
    videoError.value = t('COMMENTS.POSTS.COMPOSER.VIDEO_ERROR.TOO_LARGE', {
      size: imageMaxSizeMB.value,
    });
    return;
  }

  if (videoPreviewUrl.value) URL.revokeObjectURL(videoPreviewUrl.value);
  videoError.value = '';
  video.value = file;
  videoPreviewUrl.value = URL.createObjectURL(file);
};

// Publishing now is the common case, so "When" starts on Publish now --
// switching back to it also drops any date already entered, so a schedule
// picked and then abandoned can never ride along silently on a submit.
const whenMode = ref('now');
// A Date, not the string a native datetime-local hands back: the picker below works in
// Date objects, and every consumer here (bounds, preview, payload) wanted a Date anyway.
const scheduledAt = ref(null);
const setWhenNow = () => {
  whenMode.value = 'now';
  scheduledAt.value = null;
};
const setWhenSchedule = () => {
  whenMode.value = 'schedule';
};

// The times an agent actually reaches for. Each is a real instant computed when clicked,
// so "tomorrow" stays correct if the composer sits open across midnight.
const atHour = (date, hour) =>
  setSeconds(setMinutes(setHours(date, hour), 0), 0);
const SCHEDULE_PRESETS = [
  {
    key: 'IN_AN_HOUR',
    label: t('COMMENTS.POSTS.COMPOSER.SCHEDULE_PRESETS.IN_AN_HOUR'),
    at: () => addHours(new Date(), 1),
  },
  {
    key: 'TOMORROW_MORNING',
    label: t('COMMENTS.POSTS.COMPOSER.SCHEDULE_PRESETS.TOMORROW_MORNING'),
    at: () => atHour(startOfTomorrow(), 9),
  },
  {
    key: 'NEXT_WEEK',
    label: t('COMMENTS.POSTS.COMPOSER.SCHEDULE_PRESETS.NEXT_WEEK'),
    at: () => atHour(nextMonday(new Date()), 9),
  },
];
const applyPreset = preset => {
  scheduledAt.value = preset.at();
};
const isPresetActive = preset =>
  Boolean(scheduledAt.value) &&
  scheduledAt.value.getTime() === preset.at().getTime();

const isBeforeWindow = date =>
  isBefore(date, addMinutes(new Date(), SCHEDULE_MIN_MINUTES));
const isAfterWindow = date =>
  isAfter(date, addMonths(new Date(), SCHEDULE_MAX_MONTHS));

// `datetime-local` speaks a local, seconds-less string while everything else here works in
// Date objects, so the field is bridged rather than made the source of truth. `min`/`max`
// carry the same window the server enforces, which stops the browser's own picker from
// offering a time the API would reject; the error text below still covers a typed value,
// which no browser validates against min/max until submit.
const toDatetimeLocal = date => {
  const pad = value => String(value).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

const scheduleMin = computed(() =>
  toDatetimeLocal(addMinutes(new Date(), SCHEDULE_MIN_MINUTES))
);
const scheduleMax = computed(() =>
  toDatetimeLocal(addMonths(new Date(), SCHEDULE_MAX_MONTHS))
);

const scheduledAtLocal = computed({
  get: () => (scheduledAt.value ? toDatetimeLocal(scheduledAt.value) : ''),
  set: value => {
    scheduledAt.value = value ? new Date(value) : null;
  },
});

// The bound names in the message, not "invalid date" -- an agent who hits
// this needs to know which side of the window they missed, not just that
// something is wrong.
const scheduleError = computed(() => {
  if (!scheduledAt.value) return '';
  if (isBeforeWindow(scheduledAt.value)) {
    return t('COMMENTS.POSTS.COMPOSER.SCHEDULE_ERROR.TOO_SOON');
  }
  if (isAfterWindow(scheduledAt.value)) {
    return t('COMMENTS.POSTS.COMPOSER.SCHEDULE_ERROR.TOO_FAR');
  }
  return '';
});

const isScheduled = computed(
  () => Boolean(scheduledAt.value) && !scheduleError.value
);

const scheduledSummary = computed(() =>
  isScheduled.value
    ? t('COMMENTS.POSTS.COMPOSER.PREVIEW_SCHEDULED', {
        time: format(scheduledAt.value, 'PPPp'),
      })
    : ''
);

const submitLabel = computed(() =>
  scheduledAt.value
    ? t('COMMENTS.POSTS.COMPOSER.SCHEDULE_SUBMIT')
    : t('COMMENTS.POSTS.COMPOSER.SUBMIT')
);

const canSubmit = computed(
  () =>
    Boolean(inboxId.value) &&
    hasBrief.value &&
    !linkError.value &&
    !scheduleError.value &&
    !props.isSubmitting
);

const handleSubmit = () => {
  if (!canSubmit.value) return;
  emit('submit', {
    inboxId: inboxId.value,
    message: message.value.trim(),
    link: postType.value === 'link' ? link.value.trim() || null : null,
    image: postType.value === 'image' ? image.value : null,
    video: postType.value === 'video' ? video.value : null,
    // ISO8601, not Unix -- the store/API forward this straight through to
    // Rails' Time.zone.parse.
    scheduledAt: scheduledAt.value ? scheduledAt.value.toISOString() : null,
  });
};

// The dialog is only emptied and closed once the post actually exists. Clearing on click meant
// a Graph rejection -- an expired page token is the ordinary case -- silently destroyed the
// message, the chosen image and the schedule with nothing to paste back. A failed submit leaves
// the dialog open with everything the agent typed still in place.
const onSuccess = () => {
  message.value = '';
  link.value = '';
  removeImage();
  removeVideo();
  postType.value = 'text';
  setWhenNow();
  generationError.value = '';
  dismissImagePrompt();
  isPreviewing.value = false;
  close();
};

defineExpose({ open, close, onSuccess });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :title="t('COMMENTS.POSTS.COMPOSER.TITLE')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @confirm="handleSubmit"
  >
    <div data-testid="post-composer" class="flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <span
          class="text-xs font-medium tracking-wide uppercase text-n-slate-10"
        >
          {{ t('COMMENTS.POSTS.COMPOSER.PAGE_LABEL') }}
        </span>
        <!-- `flex-1 basis-0` over intrinsic widths: page names vary wildly in length, and
             sizing each card to its own name left the row looking like a broken table.
             The tick keeps its slot when unselected so choosing a page never reflows. -->
        <div class="flex flex-wrap gap-2">
          <button
            v-for="inbox in inboxes"
            :key="inbox.id"
            type="button"
            data-testid="composer-inbox-card"
            :data-inbox-id="inbox.id"
            :aria-pressed="inboxId === inbox.id"
            class="flex flex-1 basis-0 min-w-[190px] items-center h-14 gap-2.5 px-2.5 text-left border rounded-xl"
            :class="
              inboxId === inbox.id
                ? 'border-n-brand bg-n-brand/10'
                : 'border-n-weak bg-n-solid-1 hover:bg-n-alpha-1'
            "
            @click="inboxId = inbox.id"
          >
            <span
              class="grid text-sm font-semibold rounded-full size-9 shrink-0 place-items-center"
              :class="
                inboxId === inbox.id
                  ? 'bg-n-brand text-white'
                  : 'bg-n-solid-2 text-n-slate-11'
              "
            >
              {{ inbox.name.trim().charAt(0).toUpperCase() }}
            </span>
            <span
              data-testid="composer-inbox-card-name"
              class="flex-1 min-w-0 text-sm font-medium truncate text-n-slate-12"
            >
              {{ inbox.name }}
            </span>
            <Icon
              icon="i-lucide-check"
              class="shrink-0 size-4 text-n-blue-11"
              :class="{ 'opacity-0': inboxId !== inbox.id }"
            />
          </button>
        </div>
      </div>

      <div
        v-if="isPreviewing"
        data-testid="composer-preview"
        class="p-4 border rounded-2xl border-n-weak bg-n-solid-1"
      >
        <div class="flex items-center gap-2.5 mb-3">
          <span
            class="grid text-sm font-semibold rounded-full size-10 shrink-0 bg-n-solid-2 text-n-slate-11 place-items-center"
          >
            <template v-if="selectedInbox">
              {{ selectedInbox.name.trim().charAt(0).toUpperCase() }}
            </template>
            <Icon v-else icon="i-lucide-users-round" class="size-4" />
          </span>
          <div class="min-w-0">
            <p
              data-testid="composer-preview-page"
              class="text-sm font-semibold truncate text-n-slate-12"
            >
              {{
                selectedInbox?.name ||
                t('COMMENTS.POSTS.COMPOSER.PREVIEW_PAGE_PLACEHOLDER')
              }}
            </p>
            <p
              data-testid="composer-preview-timing"
              class="text-xs"
              :class="isScheduled ? 'text-n-amber-11' : 'text-n-slate-10'"
            >
              {{ scheduledSummary || t('COMMENTS.POSTS.COMPOSER.PREVIEW_NOW') }}
            </p>
          </div>
        </div>
        <p
          data-testid="composer-preview-message"
          class="text-base leading-relaxed whitespace-pre-wrap text-n-slate-12"
        >
          {{
            message || t('COMMENTS.POSTS.COMPOSER.PREVIEW_MESSAGE_PLACEHOLDER')
          }}
        </p>
        <img
          v-if="imagePreviewUrl"
          :src="imagePreviewUrl"
          alt=""
          data-testid="composer-preview-image"
          class="object-cover w-full mt-3 rounded-xl max-h-64 bg-n-solid-2"
        />
        <video
          v-if="videoPreviewUrl"
          :src="videoPreviewUrl"
          controls
          data-testid="composer-preview-video"
          class="w-full mt-3 rounded-xl max-h-64 bg-n-solid-2"
        />
        <p
          v-if="postType === 'link' && link.trim() && !linkError"
          data-testid="composer-preview-link"
          class="mt-3 text-sm truncate text-n-blue-11"
        >
          {{ link }}
        </p>
      </div>

      <div
        v-else
        class="flex flex-col border rounded-2xl border-n-weak bg-n-solid-1 focus-within:border-n-brand"
      >
        <textarea
          v-model="message"
          data-testid="composer-message-input"
          rows="6"
          :placeholder="t('COMMENTS.POSTS.COMPOSER.MESSAGE_PLACEHOLDER')"
          class="w-full px-4 pt-4 pb-2 text-base leading-relaxed bg-transparent border-none resize-none min-h-40 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
        />
        <div class="flex items-center gap-1 px-2.5 pb-2.5">
          <button
            type="button"
            data-testid="composer-ai-generate-button"
            :disabled="!hasBrief || isGenerating"
            :title="t('COMMENTS.POSTS.COMPOSER.AI.WRITE_TOOLTIP')"
            class="inline-flex items-center h-8 gap-1.5 px-2.5 text-xs font-medium rounded-lg text-n-blue-11 hover:bg-n-brand/10 disabled:opacity-40 disabled:hover:bg-transparent"
            @click="handleGenerate"
          >
            <Icon
              :icon="
                isGenerating ? 'i-lucide-loader-circle' : 'i-lucide-sparkles'
              "
              class="size-4"
              :class="{ 'animate-spin': isGenerating }"
            />
            {{
              isGenerating
                ? t('COMMENTS.POSTS.COMPOSER.AI.GENERATING')
                : t('COMMENTS.POSTS.COMPOSER.AI.WRITE_BUTTON')
            }}
          </button>
          <button
            type="button"
            data-testid="composer-image-prompt-generate-button"
            :disabled="!hasBrief || isGeneratingImagePrompt"
            :title="t('COMMENTS.POSTS.COMPOSER.IMAGE_PROMPT.TOOLTIP')"
            class="inline-flex items-center h-8 gap-1.5 px-2.5 text-xs font-medium rounded-lg text-n-blue-11 hover:bg-n-brand/10 disabled:opacity-40 disabled:hover:bg-transparent"
            @click="handleGenerateImagePrompt"
          >
            <Icon
              :icon="
                isGeneratingImagePrompt
                  ? 'i-lucide-loader-circle'
                  : 'i-lucide-wand-sparkles'
              "
              class="size-4"
              :class="{ 'animate-spin': isGeneratingImagePrompt }"
            />
            {{
              isGeneratingImagePrompt
                ? t('COMMENTS.POSTS.COMPOSER.IMAGE_PROMPT.GENERATING')
                : t('COMMENTS.POSTS.COMPOSER.IMAGE_PROMPT.BUTTON')
            }}
          </button>
          <span
            v-if="visibleCharacterCount"
            data-testid="composer-character-count"
            :title="t('COMMENTS.POSTS.COMPOSER.CHARACTER_COUNT_TOOLTIP')"
            class="ml-auto pr-1 text-[11px] tabular-nums"
            :class="isPastTruncation ? 'text-n-amber-11' : 'text-n-slate-10'"
          >
            <span v-if="isPastTruncation" class="mr-1.5">
              {{ t('COMMENTS.POSTS.COMPOSER.TRUNCATION_HINT') }}
            </span>
            {{
              t('COMMENTS.POSTS.COMPOSER.CHARACTER_COUNT', {
                count: visibleCharacterCount,
                limit: VISIBLE_CHARACTER_LIMIT,
              })
            }}
          </span>
        </div>
      </div>

      <span
        v-if="generationError"
        data-testid="composer-ai-error"
        class="text-xs text-n-ruby-9"
      >
        {{ generationError }}
      </span>
      <span
        v-if="imagePromptGenerationError"
        data-testid="composer-image-prompt-error"
        class="text-xs text-n-ruby-9"
      >
        {{ imagePromptGenerationError }}
      </span>

      <div
        v-if="generatedImagePrompt"
        data-testid="composer-image-prompt-output"
        class="flex flex-col gap-2 p-3 border rounded-xl border-n-weak bg-n-solid-2"
      >
        <div class="flex items-center gap-2">
          <p
            class="flex-1 text-[11px] font-semibold tracking-wide uppercase text-n-slate-10"
          >
            {{ t('COMMENTS.POSTS.COMPOSER.IMAGE_PROMPT.OUTPUT_LABEL') }}
          </p>
          <button
            type="button"
            data-testid="composer-image-prompt-copy-button"
            class="inline-flex items-center h-7 gap-1.5 px-2 text-xs font-medium rounded-lg text-n-slate-12 hover:bg-n-alpha-2"
            @click="handleCopyImagePrompt"
          >
            <Icon icon="i-lucide-copy" class="size-3.5" />
            {{ t('COMMENTS.POSTS.COMPOSER.IMAGE_PROMPT.COPY_BUTTON') }}
          </button>
          <button
            type="button"
            data-testid="composer-image-prompt-dismiss"
            :title="t('COMMENTS.POSTS.COMPOSER.IMAGE_PROMPT.DISMISS')"
            class="grid rounded-lg size-7 shrink-0 text-n-slate-11 hover:bg-n-alpha-2 place-items-center"
            @click="dismissImagePrompt"
          >
            <Icon icon="i-lucide-x" class="size-3.5" />
          </button>
        </div>
        <p
          data-testid="composer-image-prompt-text"
          class="text-sm whitespace-pre-wrap text-n-slate-12"
        >
          {{ generatedImagePrompt }}
        </p>
        <p class="text-xs text-n-slate-10">
          {{ t('COMMENTS.POSTS.COMPOSER.IMAGE_PROMPT.OUTPUT_HELP') }}
        </p>
      </div>

      <div v-if="postType === 'link'" class="flex flex-col gap-1">
        <input
          v-model="link"
          type="text"
          data-testid="composer-link-input"
          :placeholder="t('COMMENTS.POSTS.COMPOSER.LINK_PLACEHOLDER')"
          class="w-full h-10 px-3 text-sm border rounded-xl border-n-weak bg-n-solid-1 text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand focus-visible:-outline-offset-1"
        />
        <span
          v-if="linkError"
          data-testid="composer-link-error"
          class="text-xs text-n-ruby-9"
        >
          {{ linkError }}
        </span>
      </div>

      <div v-if="postType === 'image'" class="flex flex-col gap-2">
        <label
          v-if="!imagePreviewUrl"
          for="composer-image-input"
          class="flex flex-col items-center justify-center gap-1 py-8 text-sm border-2 border-dashed cursor-pointer rounded-2xl border-n-weak text-n-slate-11 hover:bg-n-alpha-1"
        >
          <Icon icon="i-lucide-image-plus" class="size-5" />
          {{ t('COMMENTS.POSTS.COMPOSER.IMAGE_BUTTON') }}
        </label>
        <input
          id="composer-image-input"
          type="file"
          accept="image/*"
          data-testid="composer-image-input"
          class="hidden"
          @change="handleImageChange"
        />
        <span
          v-if="imageError"
          data-testid="composer-image-error"
          class="text-xs text-n-ruby-9"
        >
          {{ imageError }}
        </span>
        <div
          v-if="imagePreviewUrl"
          class="relative overflow-hidden rounded-2xl"
        >
          <img
            :src="imagePreviewUrl"
            :alt="t('COMMENTS.POSTS.COMPOSER.IMAGE_PREVIEW_ALT')"
            data-testid="composer-image-preview"
            class="object-cover w-full max-h-64 bg-n-solid-2"
          />
          <button
            type="button"
            data-testid="composer-image-remove"
            :title="t('COMMENTS.POSTS.COMPOSER.IMAGE_REMOVE')"
            class="absolute grid rounded-full top-2 right-2 size-8 bg-n-slate-12/70 text-n-solid-1 hover:bg-n-slate-12 place-items-center"
            @click="removeImage"
          >
            <Icon icon="i-lucide-x" class="size-4" />
          </button>
        </div>
      </div>

      <div v-if="postType === 'video'" class="flex flex-col gap-2">
        <label
          v-if="!videoPreviewUrl"
          for="composer-video-input"
          class="flex flex-col items-center justify-center gap-1 py-8 text-sm border-2 border-dashed cursor-pointer rounded-2xl border-n-weak text-n-slate-11 hover:bg-n-alpha-1"
        >
          <Icon icon="i-lucide-video" class="size-5" />
          {{ t('COMMENTS.POSTS.COMPOSER.VIDEO_BUTTON') }}
        </label>
        <input
          id="composer-video-input"
          type="file"
          accept="video/*"
          data-testid="composer-video-input"
          class="hidden"
          @change="handleVideoChange"
        />
        <span
          v-if="videoError"
          data-testid="composer-video-error"
          class="text-xs text-n-ruby-9"
        >
          {{ videoError }}
        </span>
        <div
          v-if="videoPreviewUrl"
          class="relative overflow-hidden rounded-2xl"
        >
          <video
            :src="videoPreviewUrl"
            controls
            data-testid="composer-video-preview"
            class="w-full max-h-64 bg-n-solid-2"
          />
          <button
            type="button"
            data-testid="composer-video-remove"
            :title="t('COMMENTS.POSTS.COMPOSER.VIDEO_REMOVE')"
            class="absolute grid rounded-full top-2 right-2 size-8 bg-n-slate-12/70 text-n-solid-1 hover:bg-n-slate-12 place-items-center"
            @click="removeVideo"
          >
            <Icon icon="i-lucide-x" class="size-4" />
          </button>
        </div>
      </div>

      <!-- Attachments only. Preview and Schedule used to sit in this same bar, which put
           "what goes in the post" and "what happens to the post" on one undifferentiated
           row; they now live beside Publish in the footer, where the other verbs are. -->
      <div
        class="flex flex-wrap items-center gap-1 p-2 border rounded-xl border-n-weak"
      >
        <span class="pl-1 pr-2 text-sm font-medium text-n-slate-12">
          {{ t('COMMENTS.POSTS.COMPOSER.ADD_HEADING') }}
        </span>
        <button
          type="button"
          data-testid="composer-type-image"
          class="inline-flex items-center h-8 gap-1.5 px-2.5 text-sm font-medium rounded-lg"
          :class="
            postType === 'image'
              ? 'bg-n-brand/10 text-n-blue-11'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="togglePostType('image')"
        >
          <Icon icon="i-lucide-image" class="size-4" />
          {{ t('COMMENTS.POSTS.COMPOSER.TYPE_IMAGE') }}
        </button>
        <button
          type="button"
          data-testid="composer-type-video"
          class="inline-flex items-center h-8 gap-1.5 px-2.5 text-sm font-medium rounded-lg"
          :class="
            postType === 'video'
              ? 'bg-n-brand/10 text-n-blue-11'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="togglePostType('video')"
        >
          <Icon icon="i-lucide-video" class="size-4" />
          {{ t('COMMENTS.POSTS.COMPOSER.TYPE_VIDEO') }}
        </button>
        <button
          type="button"
          data-testid="composer-type-link"
          class="inline-flex items-center h-8 gap-1.5 px-2.5 text-sm font-medium rounded-lg"
          :class="
            postType === 'link'
              ? 'bg-n-brand/10 text-n-blue-11'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="togglePostType('link')"
        >
          <Icon icon="i-lucide-link-2" class="size-4" />
          {{ t('COMMENTS.POSTS.COMPOSER.TYPE_LINK') }}
        </button>
      </div>

      <div
        v-if="whenMode === 'schedule'"
        class="flex flex-col gap-2.5 p-3 rounded-xl bg-n-alpha-1"
      >
        <span class="text-xs text-n-slate-11">
          {{ t('COMMENTS.POSTS.COMPOSER.SCHEDULE_INPUT_LABEL') }}
        </span>
        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="preset in SCHEDULE_PRESETS"
            :key="preset.key"
            type="button"
            data-testid="composer-schedule-preset"
            :data-preset="preset.key"
            :aria-pressed="isPresetActive(preset)"
            class="h-7 px-3 text-xs font-medium border rounded-full"
            :class="
              isPresetActive(preset)
                ? 'border-n-brand bg-n-brand/10 text-n-blue-11'
                : 'border-n-weak bg-n-solid-1 text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="applyPreset(preset)"
          >
            {{ preset.label }}
          </button>
        </div>
        <input
          id="composer-schedule-input"
          v-model="scheduledAtLocal"
          type="datetime-local"
          data-testid="composer-schedule-input"
          :min="scheduleMin"
          :max="scheduleMax"
          class="h-9 px-3 text-sm border rounded-lg w-fit border-n-weak bg-n-solid-1 text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand focus-visible:-outline-offset-1"
        />
        <span
          v-if="scheduleError"
          data-testid="composer-schedule-error"
          class="text-xs text-n-ruby-9"
        >
          {{ scheduleError }}
        </span>
        <span
          v-else-if="isScheduled"
          data-testid="composer-schedule-summary"
          class="text-xs font-medium text-n-amber-11"
        >
          {{ scheduledSummary }}
        </span>
      </div>
    </div>

    <template #footer>
      <div class="flex flex-wrap items-center justify-between w-full gap-3">
        <div class="flex items-center gap-1">
          <button
            type="button"
            data-testid="composer-preview-toggle"
            :aria-pressed="isPreviewing"
            class="inline-flex items-center h-8 gap-1.5 px-2.5 text-sm font-medium rounded-lg"
            :class="
              isPreviewing
                ? 'bg-n-brand/10 text-n-blue-11'
                : 'text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="togglePreview"
          >
            <Icon
              :icon="isPreviewing ? 'i-lucide-pencil' : 'i-lucide-eye'"
              class="size-4"
            />
            {{
              isPreviewing
                ? t('COMMENTS.POSTS.COMPOSER.EDIT_BUTTON')
                : t('COMMENTS.POSTS.COMPOSER.PREVIEW_BUTTON')
            }}
          </button>
          <button
            type="button"
            data-testid="composer-when-schedule"
            :aria-pressed="whenMode === 'schedule'"
            class="inline-flex items-center h-8 gap-1.5 px-2.5 text-sm font-medium rounded-lg"
            :class="
              whenMode === 'schedule'
                ? 'bg-n-brand/10 text-n-blue-11'
                : 'text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="whenMode === 'schedule' ? setWhenNow() : setWhenSchedule()"
          >
            <Icon icon="i-lucide-calendar-clock" class="size-4" />
            {{ t('COMMENTS.POSTS.COMPOSER.WHEN_SCHEDULE') }}
          </button>
        </div>
        <div class="flex items-center gap-2">
          <Button
            :label="t('DIALOG.BUTTONS.CANCEL')"
            variant="link"
            color="slate"
            type="button"
            @click="close"
          />
          <Button
            data-testid="composer-submit-button"
            color="blue"
            type="submit"
            :label="submitLabel"
            :disabled="!canSubmit"
            :is-loading="isSubmitting"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>
